/** Undo a completed merge, from the record's own `⋯` menu (PM-9, spec §7).
 *
 * ⛔ THE ELIGIBILITY VERDICT COMES FROM THE SERVER, not from anything this component reasons about:
 * `GET /api/crm/merge-history/{kind}/{id}` calls `undo.plan_undo` — the real planner — so the button
 * is disabled WITH ITS REASON rather than failing on click. An operator who clicks Undo and receives
 * a 409 about LIFO ordering has learned nothing about what to do next.
 *
 * ⚠ THE UNDO HAS NO UNDO, and the copy says so rather than implying reversibility is free. The three
 * real limits (strict LIFO, no reversal ledger on an old merge, and a refusal when a captured row has
 * been edited since) are the route's own and are surfaced verbatim when they fire.
 */
import { useState } from 'react';
import styles from './crm.module.css';
import { ApiError } from '../../api/client';
import { useCrmMergeHistory, useCrmMergeUndo, type CrmEntityKind } from '../../hooks/useCrm';

interface Props {
  kind: CrmEntityKind;
  id: number;
  onClose: () => void;
  onUndone: () => void;
}

const errText = (e: unknown, fb: string) => {
  if (e instanceof ApiError) {
    const d = e.detail;
    if (typeof d === 'string' && d) return d;
    if (d && typeof d === 'object') {
      const rec = d as Record<string, unknown>;
      const msg = rec.message ?? rec.detail ?? rec.error;
      if (typeof msg === 'string' && msg) return msg;
    }
    return e.message || fb;
  }
  return fb;
};

export function CrmMergeUndoModal({ kind, id, onClose, onUndone }: Props) {
  const history = useCrmMergeHistory(kind, id, true);
  const undo = useCrmMergeUndo(kind);
  const [note, setNote] = useState('');
  const [typed, setTyped] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const merge = history.data?.merge ?? null;
  const verdict = history.data?.undo;
  const logId = merge?.merge_log_id ?? null;
  const armed = logId != null && typed.trim() === String(logId);
  const canApply = !!verdict?.eligible && armed && !!note.trim() && !undo.isPending;

  const apply = () => {
    if (!canApply || logId == null) return;
    setErr(null);
    undo.mutate({ merge_log_id: logId, reason_note: note.trim(), confirm_merge_log_id: logId }, {
      onSuccess: (out) => {
        if (out?.undone === true) onUndone();
        else setErr('Nothing was undone — the preview came back instead.');
      },
      onError: (e) => setErr(errText(e, 'The undo was refused.')),
    });
  };

  return (
    <div className={styles.modalBackdrop} onMouseDown={onClose}>
      <div className={styles.mergeCard} role="dialog" aria-label="Undo this merge"
           onMouseDown={(e) => e.stopPropagation()}>
        <h3 className={styles.modalTitle}>Undo this merge</h3>
        {history.isLoading && <div className={styles.mergeEmpty}>Checking…</div>}
        {history.isError && (
          <div className={styles.modalError} role="alert">Could not load this record's merge.</div>
        )}
        {merge && (
          <div className={styles.mergePreview}>
            <div className={styles.mergePreviewLine}>
              Record <strong>{merge.merged_away_id}</strong> was merged into{' '}
              <strong>{merge.survivor_id}</strong>
              {merge.merged_by ? ` by ${merge.merged_by}` : ''}
              {merge.merged_at ? ` on ${merge.merged_at.slice(0, 10)}` : ''}.
            </div>
            {merge.evidence_note && (
              <div className={styles.mergeResultMeta}>Reason given: “{merge.evidence_note}”</div>
            )}
          </div>
        )}
        {verdict && !verdict.eligible && (
          <div className={styles.modalError} role="alert">{verdict.message}</div>
        )}
        {verdict?.eligible && (
          <>
            <div className={styles.mergeWarn}>
              {verdict.message}
              {verdict.rows_to_reverse != null
                && ` About ${verdict.rows_to_reverse} rows move back.`}
              {' '}⚠ The undo has no undo.
            </div>
            <label className={styles.mergeLabel} htmlFor="crm-undo-note">Why are you reversing it?</label>
            <input id="crm-undo-note" className={styles.modalInput} value={note}
                   placeholder="Say what was wrong about the merge"
                   onChange={(e) => setNote(e.target.value)} />
            <label className={styles.mergeLabel} htmlFor="crm-undo-confirm">
              Type the merge number {logId} to confirm
            </label>
            <input id="crm-undo-confirm" className={styles.modalInput} value={typed}
                   autoComplete="off" onChange={(e) => setTyped(e.target.value)} />
          </>
        )}
        {err && <div className={styles.modalError} role="alert">{err}</div>}
        <div className={styles.modalActions}>
          <button className={styles.gridMiniBtn} onClick={onClose}>Cancel</button>
          <button className={styles.gridPageBtn} disabled={!canApply} onClick={apply}>
            {undo.isPending ? 'Reversing…' : 'Reverse this merge'}
          </button>
        </div>
      </div>
    </div>
  );
}
